import { camelizeKeys } from "humps";
import { any, omit } from "ramda";
import { BC_HQ, HQ_SERVICE, INVENTORY, PARTNERS } from "app_constants";
import { findAppFromStoreByAppName } from "helpers/brand";
import {
  BREADCRUMB_PERMISSIONS,
  hasAnalyticsPermission,
  hasBreadcrumbPermission,
  locationHasInventory,
} from "helpers/chrome/locations";
import parseBySchema, { ParserType } from "helpers/parse_by_schema";
import {
  checkStatus,
  getRequest,
  parseJSON,
  postRequest,
  putRequest,
} from "helpers/requests";
import {
  type IUnreconciledAnalyticsUser,
  type IUnreconciledBreadcrumbStore,
  type IUnreconciledBreadcrumbUsers,
  reconcileUsers,
} from "helpers/transforms/all_users";
import type { IBrandGroup } from "helpers/transforms/entities";
import type { IEntity } from "interfaces/app";

import type { IAppUser, INewUser, IPOSUserInfo } from "types/user/app_user";

// todo: is Promise<IUser[]> a valid return type?
function getAllUsers(
  currentBrandGroup: IBrandGroup
): Promise<{ users: IAppUser[]; posOnlyUsers: IPOSUserInfo[] }> {
  const { brandId } = currentBrandGroup;

  const hqServiceUsers = getHQServiceUsers(brandId);

  const analyticsUsers = any(
    hasAnalyticsPermission("viewMerchantUsers"),
    currentBrandGroup.stores
  )
    ? getAnalyticsUsers(brandId)
    : [];

  // FIXME - Determine Inventory permission necessary for fetching users
  const inventoryUsers = any(locationHasInventory, currentBrandGroup.stores)
    ? getInventoryUsers()
    : [];

  const breadcrumbUsersByLocation = getAllBreadcrumbUsers(currentBrandGroup);

  return Promise.all([
    hqServiceUsers,
    analyticsUsers,
    inventoryUsers,
    ...breadcrumbUsersByLocation,
  ]).then(([hq, analytics, inventory, ...bc]) =>
    reconcileUsers(hq, analytics, inventory, ...bc)
  );
}

function getHQServiceUsers(brandId: string): Promise<any> {
  return fetch(`${HQ_SERVICE}/brands/${brandId}/users.json`, getRequest())
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys);
}

function getAnalyticsUsers(
  brandId: string
): Promise<IUnreconciledAnalyticsUser[]> {
  return fetch(
    `${PARTNERS}/brands/${brandId}/merchant_users.json`,
    getRequest()
  )
    .then(checkStatus)
    .then(parseJSON)
    .then((users) => camelizeKeys(users) as IUnreconciledAnalyticsUser[]);
}

function getInventoryUsers(): Promise<any> {
  return fetch(`${INVENTORY}/settings?act=get_all_profiles_users`, getRequest())
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys);
}

function getAllBreadcrumbUsers(
  currentBrandGroup: IBrandGroup
): Promise<IUnreconciledBreadcrumbStore>[] {
  return currentBrandGroup.stores
    .filter((store) => {
      return (
        hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.USERS)(store) ||
        hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.VIEW_USERS_ROLES)(store)
      );
    })
    .map(getBreadcrumbStoreAndUsers);
}

function getBreadcrumbStoreAndUsers(
  store: IEntity
): Promise<IUnreconciledBreadcrumbStore> {
  const bcApp = findAppFromStoreByAppName("breadcrumb", store);

  if (!bcApp) {
    return Promise.reject();
  }
  return getBreadcrumbUsers(bcApp.storeKey).then(
    (storeUsers: IUnreconciledBreadcrumbUsers) => {
      return { storeKey: bcApp.storeKey, users: storeUsers };
    }
  );
}

function getBreadcrumbUsers(permalink: string): Promise<any> {
  return (
    fetch(`${BC_HQ}/${permalink}/pos_users.json`, getRequest())
      .then(checkStatus)
      .then(parseJSON)
      // todo: why doesn't ramda's `prop` work here?
      .then(camelizeKeys)
  );
}

// ===========================
// Creating and Updating Users
// - in hq_service
// - in single-activation flow
// ===========================

function postHQServiceUser(user: IAppUser, brand: string): Promise<IAppUser> {
  return fetch(`${HQ_SERVICE}/brands/${brand}/users`, postRequest(user))
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys)
    .then(
      parseBySchema({
        id: ParserType.STRING,
        email: ParserType.STRING,
        firstName: ParserType.STRING,
        lastName: ParserType.STRING,
      })
    );
}

function putHQServiceUser(user: IAppUser, brand: string): Promise<any> {
  return fetch(
    `${HQ_SERVICE}/brands/${brand}/users/${user.id}`,
    putRequest(user)
  )
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys);
}

function createUser(user: INewUser): Promise<any> {
  return fetch(
    `${HQ_SERVICE}/users`,
    postRequest({ user: omit(["applications"], user) })
  )
    .then(checkStatus)
    .then(parseJSON)
    .then(camelizeKeys);
}

export { createUser, getAllUsers, postHQServiceUser, putHQServiceUser };
